import React, { useEffect, useState } from 'react'
import { Row, Col, Alert, Form, FormCheck, Badge, CloseButton, Modal, Button } from 'react-bootstrap'
import Card from '../../../components/Card'

import { TxItem } from '../../../features/transactions';
import { getTransactions, getTransactionsSeek } from '../../../services/api/transaction';
import { Transaction } from '../../../services/openapi';
import { PropertyInfo } from '../../../services/openapi/models/PropertyInfo';

import { useKeycloak } from '@react-keycloak/web';

import SearchInput from '../../../components/SearchInput';
import ListPagination from '../../../components/ListPagination';
import RefreshPull from '../../../components/RefreshPull';
import CsvExport from '../../../components/exports/CsvExport';
import { ApiResult } from '../../../services/openapi/core/ApiResult';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import {
    peekPendingSelectedId,
    clearPendingSelectedId,
    setPendingListState,
    peekPendingListState,
    clearPendingListState
} from '../../../hooks/pendingSelection';
import { Link, useNavigate } from 'react-router-dom';
import { resetNavContext } from '../../../hooks/navContext';
import { useTranslation } from 'react-i18next'; 
import ListSeekPagination from '../../../components/ListSeekPagination';

type SeekPagingState = {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasPrevious: boolean;
    hasNext: boolean;
    firstId?: string;
    lastId?: string;
};

type TransactionsListSnapshot = {
    transactions: Transaction[];
    paging: SeekPagingState;
    transactionCount: string;
    pagingString: string;
};

const emptyPaging: SeekPagingState = {
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    hasPrevious: false,
    hasNext: false
};

// Seek/cursor pagination has no "go to page N" command (only Begin/Previous/
// Next/End from a cursor), so returning to the exact page the user had open
// means restoring what was already on screen rather than re-fetching it.
// Only applies when the user actually pressed "Back" on the details page -
// same gate as the row selection itself.
const getPendingTransactionsSnapshot = (): TransactionsListSnapshot | null => {
    if (peekPendingSelectedId("transactions") === null) {
        return null;
    }
    return peekPendingListState<TransactionsListSnapshot>("transactions");
};

const Transactions = () => {
    const [transactions, setTransactions] = useState<Transaction[]>(() => getPendingTransactionsSnapshot()?.transactions ?? []);
    const [props, setProps] = useState<PropertyInfo>();
    const [transactionCount, setTransactionCount] = useState<string>(() => getPendingTransactionsSnapshot()?.transactionCount ?? "0");
    const [pagingString, setPagingString] = useState<string>(() => getPendingTransactionsSnapshot()?.pagingString ?? "0-25");
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
    const [filterDraft, setFilterDraft] = useState<string>("");
    const [formData, setFormData] = useLocalStorage<string>("transactions", "formData", "");
    const [searchData, setSearchData] = useLocalStorage<Record<string, string>>("transactions", "searchData", {});
    const [filterData, setFilterData] = useLocalStorage<string>("transactions", "filterData", "");
    const [paymentCorrectionFilter, setPaymentCorrectionFilter] = useLocalStorage<boolean>("transactions", "paymentCorrectionFilter", false);
    const [paymentCorrectionFilterDraft, setPaymentCorrectionFilterDraft] = useState<boolean>(false);
    const [terminalIdentifierFilter, setTerminalIdentifierFilter] = useLocalStorage<string>("transactions", "terminalIdentifierFilter", "");
    const [terminalIdentifierFilterDraft, setTerminalIdentifierFilterDraft] = useState<string>("");
    const [currentPage, setCurrentPage] = useLocalStorage<number>("transactions", "currentPage", 0);
    const [siteFilter, setSiteFilter] = useLocalStorage<number[]>("sites", "siteFilter", []);
    const [siteFilterQuery, setSiteFilterQuery] = useLocalStorage<string>("sites", "siteFilterQuery", "");
    const [errorMsg, setErrorMsg] = useState<string[]>([]);
    const [selectedTxId, setSelectedTxId] = useState<number | null>(() => peekPendingSelectedId("transactions"));
    const [rowClickCount, setRowClickCount] = useState({});
    // Captured once at mount, before any effect gets a chance to clear the
    // pending flags - tells the initial-load effect below whether the state
    // above was just restored from a "Back" navigation (and should not be
    // clobbered by an immediate page-1 fetch).
    const [restoredPageFromBack] = useState<boolean>(() => getPendingTransactionsSnapshot() !== null);

    const pageSize = 25;

    const [paging, setPaging] = useState<SeekPagingState>(() => getPendingTransactionsSnapshot()?.paging ?? emptyPaging);

    const { keycloak, initialized } = useKeycloak();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const delay = async (ms: number) => {
        await new Promise(resolve => setTimeout(resolve, ms));
    }

    const csvHeaders = [
        { label: 'Site ID', key: 'siteId'},
        { label: 'POI', key: 'terminalIdentifier' },
        { label: 'Terminal', key: 'terminalDescription'},
        { label: i18n.language === 'de' ? 'Datum und Uhrzeit' : i18n.language === 'en' ? 'Date and Time' : 'Datum en tijd', key: 'modified' },        
        { label: i18n.language === 'de' ? 'Betrag' : i18n.language === 'en' ? 'Amount' : 'Bedrag', key: 'chargedAmount' },
        { label: i18n.language === 'de' ? 'Fehlercode' : i18n.language === 'en' ? 'Error Code' :    'Foutcode', key: 'terminalStatusCode' },
        { label: i18n.language === 'de' ? 'Produkt' : i18n.language === 'en' ? 'Product' : 'Product', key: 'productId' },
        { label: i18n.language === 'de' ? 'Marke karte' : i18n.language === 'en' ? 'Brand Card' : 'Merk kaart', key: 'paymentMethod' }
    ];

    let timer: NodeJS.Timeout | null = null;

    const submitSearch = () => {
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const setData = (data: Transaction, props: PropertyInfo) => {        
        localStorage.setItem('Transaction', JSON.stringify(data));
        localStorage.setItem('PropertyInfo', JSON.stringify(props));
    }

    const handleChange = () => {
        if (timer) {
            clearTimeout(timer)
        }
        timer = setTimeout(submitSearch, 2000);
        //setCurrentPage(0);
        handleSeekPaging('Begin');
    }

    const handleFilter = (terminalStatusCode: string, paymentCorrectionNeeded: boolean, terminalIdentifier: string): void => {
        setTransactions([]);
        setFilterData(terminalStatusCode);
        setPaymentCorrectionFilter(paymentCorrectionNeeded);
        setTerminalIdentifierFilter(terminalIdentifier);
        //setCurrentPage(0);
        handleSeekPaging('Begin', { filterData: terminalStatusCode, paymentCorrectionNeeded, terminalIdentifierFilter: terminalIdentifier });
    }

    const handleSearch = (e: string): void => {
        setTransactions([]);
        searchData["search"] = e;
        setSearchData(searchData);
        formData === "" ? setFormData(" ") : setFormData("");
        //setCurrentPage(0);
        handleSeekPaging('Begin');
    }

    const handleSearchColumnModified = (e: string): void => {
        setTransactions([]);
        searchData["modified"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumnDeliveryStatus = (e: string): void => {
        setTransactions([]);
        searchData["deliveryStatus"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumnProduct = (e: string): void => {
        setTransactions([]);
        searchData["productId"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumnVmc = (e: string): void => {
        setTransactions([]);
        searchData["vmcName"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumnAmount = (e: string): void => {
        setTransactions([]);
        searchData["chargedAmountInCents"] = e.replace('.', ',');
        setSearchData(searchData);
        handleChange();
    }

    const handleRowClick = (tx) => { 
        if (window.matchMedia('(pointer: coarse)').matches) {
            setRowClickCount({ [tx.id]: 1 });
        }
        
        setRowClickCount((prev) => {
            const newCount = (prev[tx.id] || 0) + 1;
            if (newCount === 1) {
            setSelectedTxId(tx.id);
            return { ...prev, [tx.id]: newCount };
            } else if (newCount === 2) {
            setData(tx, props);
            setSelectedTxId(tx.id);
            setPendingListState<TransactionsListSnapshot>("transactions", { transactions, paging, transactionCount, pagingString });
            resetNavContext({ from: 'lists/transactions', id: null });
            navigate("/dashboard/details/transaction");
            return { ...prev, [tx.id]: 0 };
            }
            return prev;
        });
    };

    const handleRefreshButton = (): void => {
        setTransactions([]);
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const handleSeekPaging = async (
        command: 'Begin' | 'Previous' | 'Next' | 'End',
        overrides?: {
            siteFilter?: number[];
            siteFilterQuery?: string;
            filterData?: string;
            paymentCorrectionNeeded?: boolean;
            terminalIdentifierFilter?: string;
        }
    ) => {
        errorMsg.length = 0;

        do {
            await delay(1000);
        } while (isRefreshing);

        document.querySelector('#refresh')?.classList.remove('d-none');
        setIsRefreshing(true);

        const effectiveSiteFilter = overrides?.siteFilter ?? siteFilter;
        const effectiveSiteFilterQuery = overrides?.siteFilterQuery ?? siteFilterQuery;
        const effectiveFilterData = overrides?.filterData ?? filterData;
        const effectivePaymentCorrectionNeeded = overrides?.paymentCorrectionNeeded ?? paymentCorrectionFilter;
        const effectiveTerminalIdentifierFilter = overrides?.terminalIdentifierFilter ?? terminalIdentifierFilter;

        const t1 = performance.now();

        getTransactionsSeek(
            keycloak.token || '',
            {
                command,
                pageSize,
                currentPage: paging.currentPage,
                firstId: paging.firstId,
                lastId: paging.lastId
            },
            {
                search: formData,
                terminalStatusCode: effectiveFilterData,
                paymentCorrectionNeeded: effectivePaymentCorrectionNeeded ? "true" : "",
                terminalIdentifier: effectiveTerminalIdentifierFilter,
                sites: effectiveSiteFilter.join(),
                sitesQuery: effectiveSiteFilterQuery,
                languageCode: i18n.language
            },
            searchData
        )
            .then((result) => {
                const t2 = performance.now();
                console.log('Get transactions took ' + (t2 - t1) + ' ms.');

                const response = result.body;

                setTransactions(response.body);
                setProps(response.propertyNames);
                setPaging({
                    currentPage: response.currentPage,
                    totalPages: response.totalPages,
                    totalCount: response.totalCount,
                    hasPrevious: response.hasPrevious,
                    hasNext: response.hasNext,
                    firstId: response.firstId,
                    lastId: response.lastId
                });

                setTransactionCount(String(response.totalCount));
                setCurrentPage(response.currentPage - 1);
                setPagingString(result.header);

                document.querySelector('#refresh')?.classList.add('d-none');
                setIsRefreshing(false);
            })
            .catch((error) => {
                try {
                    document.querySelector('.errorMsg')?.classList.remove('d-none');
                    Object.entries<Record<string, any>>(JSON.parse(error.body)['errors']).forEach((k) => {
                        k[1].forEach((msg: string) => {
                            errorMsg.push(msg);
                            setErrorMsg(errorMsg);
                        });
                    });
                } catch {
                    errorMsg.push('Error handling response');
                    setErrorMsg(errorMsg);
                }

                document.querySelector('#refresh')?.classList.add('d-none');
                setIsRefreshing(false);
            });
    };
    const handlePaging = async (e: number) => {
        errorMsg.length = 0;

        do {
            await delay(1000);
        } while (isRefreshing);

        setCurrentPage(e);

        const start = ((e + 1) * 25) - 25;
        const end = ((e + 1) * 25);
        const rangeString = `${start}-${end}`
        setPagingString(rangeString);

        document.querySelector('#refresh')?.classList.remove("d-none");
        setIsRefreshing(true);
        const t1 = performance.now();
        getTransactions(keycloak.token || '', rangeString, { search: formData, terminalStatusCode: filterData, terminalIdentifier: terminalIdentifierFilter, sites: siteFilter.join(), sitesQuery: siteFilterQuery, languageCode: i18n.language }, searchData)
            .then((result) => {
                const t2 = performance.now();
                console.log('Get transactions took ' + (t2 - t1) + ' ms.');
                setTransactions(result.body);
                var totalCount = result.header.split('/')[1];
                setTransactionCount(totalCount)
                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);
            })
            .catch((error) => {
                try {
                    document.querySelector('.errorMsg')?.classList.remove("d-none");
                    Object.entries<Record<string, any>>(JSON.parse(error.body)["errors"]).forEach((k) => { k[1].forEach((msg: string) => { errorMsg.push(msg); setErrorMsg(errorMsg); }) });
                } catch (ex) {
                    errorMsg.push("Error handling response");
                    setErrorMsg(errorMsg);
                }

                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);
            });
    }

    const handleCsvExport = (): Promise<ApiResult> => {
        const rangeString = "0";
        return getTransactions(keycloak.token || '', rangeString, { search: formData, terminalStatusCode: filterData, paymentCorrectionNeeded: paymentCorrectionFilter ? "true" : "", terminalIdentifier: terminalIdentifierFilter, export: "", sites: siteFilter.join(), sitesQuery: siteFilterQuery, languageCode: i18n.language }, searchData);
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>): boolean => {
        if (event.key === 'Enter') {
            event.preventDefault();
            return false;
        }
        return true;
    }

    const init = () => {
        document.querySelector('.errorMsg')?.classList.add("d-none");
        handlePaging(currentPage ?? 0);
        initSearchFields();
    };

    const initSearchFields = () => {
        Object.keys(searchData).map(x => {
            const search: HTMLInputElement | null = document.querySelector(`.${x}`) as HTMLInputElement;
            if (search) {
                search.value = searchData[x];
            }
        })
    }

    const onResize = () => {
        var e = document.querySelector(".vertical-scrollable") as HTMLElement;
        var h = window.innerHeight;
        e.style.height = (h - 284) + "px";
        e.style.maxHeight = (h - 280) + "px";
    };

    useEffect(() => {
        window.addEventListener("resize", onResize);
        if (transactions?.length === 0) {
            //init();
        }
        clearPendingSelectedId("transactions");
        clearPendingListState("transactions");
        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, []);

    useEffect(() => {
        if (transactions.length > 0) return;
        //init();
    }, [formData, filterData]);

    useEffect(() => {
        // If we just restored the page the user was on before opening a
        // transaction's details (see restoredPageFromBack above), that data
        // is already on screen - don't immediately overwrite it with page 1.
        if (restoredPageFromBack) return;
        handleSeekPaging('Begin');
    }, []);

    const handleBegin = async () => {
        await handleSeekPaging('Begin');
    };

    const handlePrevious = async () => {
        if (!paging.hasPrevious) return;
        await handleSeekPaging('Previous');
    };

    const handleNext = async () => {
        if (!paging.hasNext) return;
        await handleSeekPaging('Next');
    };

    const handleEnd = async () => {
        if (paging.totalPages === 0) return;
        await handleSeekPaging('End');
    };

    return (
        <>
            <Row>
                <Col className="">
                    <Card className="rounded">
                        <Card.Body className="">
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row>
                                <Col className="">
                                    <h4 className="mb-2 qvend-title">{t("transacties")}</h4>
                                </Col>
                            </Row>
                            <Row style={{ fontSize: '11px' }} >
                                <Col xs={5} sm={5} md={5} style={{ paddingRight: 0 }}>
                                    <div className="bd-example">
                                        <div>
                                            <SearchInput className="form-control search" type="search" placeholder={t("search")} onChange={handleSearch} value={searchData.search ?? ""} />
                                        </div>
                                    </div>
                                </Col>
                                <Col md={1} xs={1}>
                                    <button type="button" className="btn btn-qvend" style={{ fontSize: '11px', padding: '0.5rem 0.3rem' }} onClick={() => { setFilterDraft(filterData); setPaymentCorrectionFilterDraft(paymentCorrectionFilter); setTerminalIdentifierFilterDraft(terminalIdentifierFilter); setShowFilterModal(true); }}>...</button>
                                </Col>
                                <Col>
                                <span className="text-end d-inline-flex align-items-center flex-wrap gap-1">
                                {((siteFilter && siteFilter.length > 0) || (siteFilterQuery && siteFilterQuery.length > 2)) &&
                                    <Badge pill bg="dark" className="p-1 d-inline-flex align-items-center gap-1" onClick={() => { setSiteFilter([]); setSiteFilterQuery(""); setTransactions([]); formData === "" ? setFormData(" ") : setFormData(""); window.localStorage.removeItem("searchDataSites"); handleSeekPaging('Begin', {siteFilter:[], siteFilterQuery:""}); }}><CloseButton variant="white" />Sites</Badge>
                                }
                                {filterData && filterData.length > 0 &&
                                    <Badge pill bg="dark" className="p-1 d-inline-flex align-items-center gap-1" onClick={() => { setFilterData(""); setTransactions([]); handleSeekPaging('Begin', { filterData: "" }); }}><CloseButton variant="white" />{t('transaction.terminalStatusCode')}: {filterData}</Badge>
                                }
                                {paymentCorrectionFilter &&
                                    <Badge pill bg="dark" className="p-1 d-inline-flex align-items-center gap-1" onClick={() => { setPaymentCorrectionFilter(false); setTransactions([]); handleSeekPaging('Begin', { paymentCorrectionNeeded: false }); }}><CloseButton variant="white" />{t('transaction.paymentCorrectionNeeded')}</Badge>
                                }
                                {terminalIdentifierFilter && terminalIdentifierFilter.length > 0 &&
                                    <Badge pill bg="dark" className="p-1 d-inline-flex align-items-center gap-1" onClick={() => { setTerminalIdentifierFilter(""); setTransactions([]); handleSeekPaging('Begin', { terminalIdentifierFilter: "" }); }}><CloseButton variant="white" />{t('transaction.terminalIdentifier')}: {terminalIdentifierFilter}</Badge>
                                }
                                </span>
                                </Col>
                                <Col className="text-end">
                                    <CsvExport onGetData={handleCsvExport} headers={csvHeaders} separator={";"} filename={"betalingen.csv"} className="btn btn-qvend" style={{ fontSize: '11px' }} />
                                </Col>                                
                            </Row>
                            <Row className="">
                                <Col sm="12" xs="12" className="mt-2">
                                    <RefreshPull isRefreshing={isRefreshing} onSetPage={() => handleSeekPaging('Begin')} />
                                    <div className="table-responsive-lg" style={{ overflowX: 'hidden' }}>
                                        <div>
                                            <Row className="qvend-table-font" style={{ background: "#2b2e83", color: "#FFFFFF", overflowY: "scroll" }}>
                                                <Col xl={2} lg={2} md={3} sm={5} xs={5} className="qvend-table table-column-first">{t('transaction.time')}</Col>                                                
                                                <Col xl={3} lg={3} md={2} sm={0} xs={0} className="qvend-table d-none d-md-block">{t('transaction.vmc')}</Col>
                                                <Col xl={3} lg={3} md={2} sm={4} xs={4} className="qvend-table">{t('transaction.status')}</Col>
                                                <Col xl={3} lg={3} md={3} sm={0} xs={0} className="qvend-table d-none d-md-block">{t('transaction.Product')}</Col>
                                                <Col xl={1} lg={1} md={2} sm={3} xs={3} className="qvend-table">{t('transaction.amount')}</Col>
                                            </Row>
                                        </div>
                                        <div className="vertical-scrollable" style={{ overscrollBehaviorY: 'contain' }}>
                                            <Row className={("qvend-table-font")} style={{ background: "#cfd5ea", color: "black" }}>
                                                <Col xl={2} lg={2} md={3} sm={5} xs={5} className="qvend-table search-in-row-first"><SearchInput className="form-control modified" type="search" onChange={handleSearchColumnModified} value={searchData.modified ?? ""}/></Col>                                                
                                                <Col xl={3} lg={3} md={2} sm={0} xs={0} className="qvend-table search-in-row-padding d-none d-md-block"><SearchInput className="form-control vmcName" type="search" onChange={handleSearchColumnVmc} value={searchData.vmcName ?? ""} /></Col>
                                                <Col xl={3} lg={3} md={2} sm={4} xs={4} className="qvend-table search-in-row-padding">
                                                    <Form.Select
                                                        className="form-control"                                                       
                                                        value={searchData.deliveryStatus ?? ""}                                                    
                                                        required={false}
                                                        onChange={(e) => handleSearchColumnDeliveryStatus(e.target.value)}                                        
                                                    >
                                                        <option key='' value=''>
                                                        {t('transactions.filter.all')}
                                                        </option>
                                                        <option key='0' value='0'>
                                                        {t('transactions.filter.waitingForPayment')}
                                                        </option>
                                                        <option key='2' value='2'>
                                                        {t('transactions.filter.reservationSucceeded')}
                                                        </option>
                                                        <option key='5' value='5'>
                                                        {t('transactions.filter.deliveryStarted')}
                                                        </option>
                                                        <option key='10' value='10'>
                                                        {t('transactions.filter.deliveryStopped')}
                                                        </option>
                                                        <option key='20' value='20'>
                                                        {t('transactions.filter.paymentSucceeded')}
                                                        </option>
                                                        <option key='25' value='25'>
                                                        {t('transactions.filter.paymentFailed')}
                                                        </option>
                                                        <option key='30' value='30'>
                                                        {t('transactions.filter.deliveryFailed')}
                                                        </option>
                                                        <option key='99' value='99'>
                                                        {t('transactions.filter.completed')}
                                                        </option>
                                                        <option key='paymentCorrectionNeeded' value='paymentCorrectionNeeded'>
                                                        {t('transactions.filter.paymentCorrectionNeeded')}
                                                        </option>
                                                    </Form.Select>                                                    
                                                </Col>
                                                <Col xl={3} lg={3} md={3} sm={0} xs={0} className="qvend-table search-in-row-padding d-none d-md-block"><SearchInput className="form-control productId" type="search" onChange={handleSearchColumnProduct} value={searchData.productId ?? ""} /></Col>
                                                <Col xl={1} lg={1} md={2} sm={3} xs={3} className="qvend-table search-in-row-padding"><SearchInput className="form-control align-right chargedAmountInCents" type="search" onChange={handleSearchColumnAmount} value={searchData.chargedAmountInCents ?? ""} /></Col>
                                            </Row>
                                            <Row className="d-none errorMsg">
                                                <Col>
                                                    {
                                                        errorMsg.map((item, index) => {
                                                            return <Alert key={index} variant="warning" className="alert" role="alert">
                                                                <span>{item}</span>
                                                            </Alert>;
                                                        })
                                                    }
                                                </Col>
                                            </Row>
                                            {transactions.map((tx, index) => (
                                                <Link
                                                    to="#"
                                                    onClick={() => handleRowClick(tx)}
                                                    key={tx.id}
                                                >
                                                    <TxItem
                                                    isSelected={selectedTxId === tx.id}
                                                    tx={tx}
                                                    rowColor={selectedTxId === tx.id ? "gray" : index % 2 ? "#cfd5ea" : "#e9ebf5"}
                                                    />
                                                </Link>
                                                ))}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <ListSeekPagination
                                articlesCount={Number(transactionCount)}
                                currentPage={paging.currentPage}
                                hasPrevious={paging.hasPrevious}
                                hasNext={paging.hasNext}
                                isRefreshing={isRefreshing}
                                onCommand={handleSeekPaging}
                                onRefresh={() => handleSeekPaging('Begin')}
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)}>
                <Form onSubmit={(e) => { e.preventDefault(); handleFilter(filterDraft, paymentCorrectionFilterDraft, terminalIdentifierFilterDraft); setShowFilterModal(false); }}>
                    <Modal.Header closeButton>
                        <Modal.Title>{t('transactions.filter.title')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group>
                            <Form.Label>{t('transaction.terminalStatusCode')}</Form.Label>
                            <Form.Control type="text" value={filterDraft} onChange={(e) => setFilterDraft(e.target.value)} />
                        </Form.Group>
                        <Form.Group className="mt-3">
                            <Form.Label>{t('transaction.terminalIdentifier')}</Form.Label>
                            <Form.Control type="text" value={terminalIdentifierFilterDraft} onChange={(e) => setTerminalIdentifierFilterDraft(e.target.value)} />
                        </Form.Group>
                        <Form.Group className="mt-3">
                            <Form.Check
                                type="checkbox"
                                id="paymentCorrectionNeededFilter"
                                label={t('transaction.paymentCorrectionNeeded')}
                                checked={paymentCorrectionFilterDraft}
                                onChange={(e) => setPaymentCorrectionFilterDraft(e.target.checked)}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowFilterModal(false)}>{t('transactions.filter.close')}</Button>
                        <Button type="submit" className="btn-qvend">{t('transactions.filter.apply')}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    )
}

export default Transactions
